const NUTRIENT_FIELDS = Object.freeze({
  potassium: ["potassium_mg_snapshot", "potassium_mg"],
  phosphorus: ["phosphorus_mg_snapshot", "phosphorus_mg"],
  sodium: ["sodium_mg_snapshot", "sodium_mg"],
  protein: ["protein_g_snapshot", "protein_g"],
});

export const V2_DASHBOARD_STATUS = Object.freeze({
  safe: "safe",
  watch: "watch",
  over: "over",
  notConfigured: "notConfigured",
});

const DEFAULT_COPY = Object.freeze({
  nutrients: {
    potassium: "Potassium",
    phosphorus: "Phosphorus",
    sodium: "Sodium",
    protein: "Protein",
  },
  statuses: {
    safe: "Within range",
    watch: "Getting close",
    over: "Over limit",
    notConfigured: "Target not configured",
  },
  safety: {
    noLogsTitle: "No food logged today.",
    noLogsDetail:
      "Based on today's tracked entries, no nutrient totals are available yet. Add food logs to review your configured ranges.",
    noTargetsTitle: "Targets are not configured.",
    noTargetsDetail: "Set daily targets with your renal dietitian or medical team before using the range summary.",
    safeTitle: "Within the configured tracking range.",
    safeDetail:
      "Based on today's tracked entries, all configured nutrient totals are below 70% of their daily limits.",
    watchTitle: "{nutrients} {verb} getting close.",
    watchDetail: "Based on today's tracked entries, one or more nutrients are getting close to the configured daily limit.",
    overTitle: "{nutrients} {verb} over the configured limit.",
    overDetail: "Based on today's tracked entries, review the over-limit nutrients and confirm personal targets with your medical team.",
    missingTargetsDetail: "Some targets are not configured, so the summary is limited to configured nutrients.",
  },
  periods: {
    today: "Today",
    yesterday: "Yesterday",
    last7: "Last 7 days",
  },
  listAnd: "and",
});

export function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function getLocalDateKey(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function getLogDateKey(log = {}) {
  const rawDate = log.logged_for_date || log.logged_at || log.created_at;
  if (!rawDate) return "";

  if (typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
    return rawDate;
  }

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "";

  return getLocalDateKey(parsed);
}

export function getPreviousLocalDateKey(referenceDate = new Date()) {
  const date = referenceDate instanceof Date ? new Date(referenceDate) : new Date(referenceDate);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() - 1);
  return getLocalDateKey(date);
}

export function filterLogsForActiveSession(logs = [], { isGuest = false, userId = "" } = {}) {
  if (!Array.isArray(logs)) return [];
  if (isGuest) return logs;

  const activeUserId = String(userId || "").trim();
  if (!activeUserId) return [];

  return logs.filter((log) => String(log?.user_id || "").trim() === activeUserId);
}

function getNutrientValue(log, nutrientKey) {
  const fields = NUTRIENT_FIELDS[nutrientKey] || [];
  const field = fields.find((candidate) => log?.[candidate] !== undefined && log?.[candidate] !== null);
  return field ? toNumber(log[field]) : 0;
}

export function filterLogsByDate(logs = [], dateKey = getLocalDateKey()) {
  if (!Array.isArray(logs) || !dateKey) return [];
  return logs.filter((log) => getLogDateKey(log) === dateKey);
}

export function filterLogsForLastDays(logs = [], days = 7, referenceDate = new Date()) {
  if (!Array.isArray(logs)) return [];

  const end = referenceDate instanceof Date ? new Date(referenceDate) : new Date(referenceDate);
  if (Number.isNaN(end.getTime())) return [];
  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setDate(start.getDate() - Math.max(days - 1, 0));
  start.setHours(0, 0, 0, 0);

  return logs.filter((log) => {
    const key = getLogDateKey(log);
    if (!key) return false;

    const logDate = new Date(`${key}T00:00:00`);
    return logDate >= start && logDate <= end;
  });
}

export function sumNutrients(logs = []) {
  return Object.keys(NUTRIENT_FIELDS).reduce((totals, nutrientKey) => {
    totals[nutrientKey] = Array.isArray(logs)
      ? logs.reduce((sum, log) => sum + getNutrientValue(log, nutrientKey), 0)
      : 0;
    return totals;
  }, {});
}

export function calculatePercentage(consumed, limit) {
  const safeLimit = toNumber(limit);
  if (safeLimit <= 0) return 0;

  return Math.round((toNumber(consumed) / safeLimit) * 100);
}

export function calculateRemaining(consumed, limit) {
  return Math.max(toNumber(limit) - toNumber(consumed), 0);
}

export function getNutrientStatus(percentage) {
  const safePercentage = toNumber(percentage);
  if (safePercentage >= 100) return V2_DASHBOARD_STATUS.over;
  if (safePercentage >= 70) return V2_DASHBOARD_STATUS.watch;
  return V2_DASHBOARD_STATUS.safe;
}

export function getStatusLabel(status, copy = DEFAULT_COPY) {
  const statuses = copy.statuses || DEFAULT_COPY.statuses;
  return statuses[status] || statuses.safe || DEFAULT_COPY.statuses.safe;
}

export function buildNutrientCards(totals = {}, targets = {}, copy = DEFAULT_COPY) {
  const labels = copy.nutrients || DEFAULT_COPY.nutrients;
  const definitions = [
    {
      key: "potassium",
      label: labels.potassium || DEFAULT_COPY.nutrients.potassium,
      unit: "mg",
      limit: targets?.potassium_limit_mg,
    },
    {
      key: "phosphorus",
      label: labels.phosphorus || DEFAULT_COPY.nutrients.phosphorus,
      unit: "mg",
      limit: targets?.phosphorus_limit_mg,
    },
    {
      key: "sodium",
      label: labels.sodium || DEFAULT_COPY.nutrients.sodium,
      unit: "mg",
      limit: targets?.sodium_limit_mg,
    },
    {
      key: "protein",
      label: labels.protein || DEFAULT_COPY.nutrients.protein,
      unit: "g",
      limit: targets?.protein_limit_g,
    },
  ];

  return definitions.map((definition) => {
    const consumed = toNumber(totals?.[definition.key]);
    const limit = toNumber(definition.limit);
    const targetConfigured = limit > 0;
    const percentage = targetConfigured ? calculatePercentage(consumed, limit) : 0;
    const status = targetConfigured ? getNutrientStatus(percentage) : V2_DASHBOARD_STATUS.notConfigured;

    return {
      ...definition,
      consumed,
      limit,
      remaining: calculateRemaining(consumed, limit),
      percentage,
      progress: Math.min(percentage, 100),
      status,
      statusLabel: getStatusLabel(status, copy),
      targetConfigured,
    };
  });
}

export function buildSafetySummary(cards = [], { hasLogs = true, copy = DEFAULT_COPY } = {}) {
  const safetyCopy = copy.safety || DEFAULT_COPY.safety;
  const configuredCards = cards.filter((card) => card.targetConfigured);
  const missingTargets = cards.filter((card) => !card.targetConfigured);

  if (!hasLogs) {
    return {
      tone: V2_DASHBOARD_STATUS.safe,
      title: safetyCopy.noLogsTitle || DEFAULT_COPY.safety.noLogsTitle,
      detail: safetyCopy.noLogsDetail || DEFAULT_COPY.safety.noLogsDetail,
      state: "noLogs",
    };
  }

  if (configuredCards.length === 0) {
    return {
      tone: V2_DASHBOARD_STATUS.notConfigured,
      title: safetyCopy.noTargetsTitle || DEFAULT_COPY.safety.noTargetsTitle,
      detail: safetyCopy.noTargetsDetail || DEFAULT_COPY.safety.noTargetsDetail,
      state: "noTargets",
    };
  }

  const over = configuredCards.filter((card) => card.status === V2_DASHBOARD_STATUS.over);
  const watch = configuredCards.filter((card) => card.status === V2_DASHBOARD_STATUS.watch);
  const missingDetail = missingTargets.length > 0 ? ` ${safetyCopy.missingTargetsDetail || DEFAULT_COPY.safety.missingTargetsDetail}` : "";

  if (over.length > 0) {
    return {
      tone: V2_DASHBOARD_STATUS.over,
      title: formatTemplate(safetyCopy.overTitle || DEFAULT_COPY.safety.overTitle, {
        nutrients: formatList(over.map((card) => card.label), copy),
        verb: over.length === 1 ? "is" : "are",
      }),
      detail: `${safetyCopy.overDetail || DEFAULT_COPY.safety.overDetail}${missingDetail}`,
      state: "over",
    };
  }

  if (watch.length > 0) {
    return {
      tone: V2_DASHBOARD_STATUS.watch,
      title: formatTemplate(safetyCopy.watchTitle || DEFAULT_COPY.safety.watchTitle, {
        nutrients: formatList(watch.map((card) => card.label), copy),
        verb: watch.length === 1 ? "is" : "are",
      }),
      detail: `${safetyCopy.watchDetail || DEFAULT_COPY.safety.watchDetail}${missingDetail}`,
      state: "watch",
    };
  }

  if (missingTargets.length > 0) {
    return {
      tone: V2_DASHBOARD_STATUS.notConfigured,
      title: safetyCopy.noTargetsTitle || DEFAULT_COPY.safety.noTargetsTitle,
      detail: `${safetyCopy.safeDetail || DEFAULT_COPY.safety.safeDetail} ${safetyCopy.missingTargetsDetail || DEFAULT_COPY.safety.missingTargetsDetail}`,
      state: "partialTargets",
    };
  }

  return {
    tone: V2_DASHBOARD_STATUS.safe,
    title: safetyCopy.safeTitle || DEFAULT_COPY.safety.safeTitle,
    detail: safetyCopy.safeDetail || DEFAULT_COPY.safety.safeDetail,
    state: "safe",
  };
}

export function buildPeriodStats(logs = [], targets = {}, referenceDate = new Date(), copy = DEFAULT_COPY) {
  const todayKey = getLocalDateKey(referenceDate);
  const yesterdayKey = getPreviousLocalDateKey(referenceDate);
  const periodCopy = copy.periods || DEFAULT_COPY.periods;

  const periods = [
    { key: "today", label: periodCopy.today || DEFAULT_COPY.periods.today, logs: filterLogsByDate(logs, todayKey) },
    { key: "yesterday", label: periodCopy.yesterday || DEFAULT_COPY.periods.yesterday, logs: filterLogsByDate(logs, yesterdayKey) },
    { key: "last7", label: periodCopy.last7 || DEFAULT_COPY.periods.last7, logs: filterLogsForLastDays(logs, 7, referenceDate) },
  ];

  return periods.map((period) => {
    const totals = sumNutrients(period.logs);
    const cards = buildNutrientCards(totals, targets, copy);
    const configuredCards = cards.filter((card) => card.targetConfigured);
    const averageLimitUse = configuredCards.length
      ? Math.round(configuredCards.reduce((sum, card) => sum + card.percentage, 0) / configuredCards.length)
      : 0;

    return {
      ...period,
      totals,
      averageLimitUse,
      averageUsed: averageLimitUse,
      hasConfiguredTargets: configuredCards.length > 0,
      hasLogs: period.logs.length > 0,
      logCount: period.logs.length,
    };
  });
}

function formatTemplate(template, values) {
  return Object.entries(values).reduce((result, [key, value]) => result.replace(`{${key}}`, value), template);
}

function formatList(items = [], copy = DEFAULT_COPY) {
  const andText = copy.listAnd || DEFAULT_COPY.listAnd;
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} ${andText} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, ${andText} ${items[items.length - 1]}`;
}
